// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierFolk3D.jsx   ·   TERROR'S PEOPLE, ON THE PIER
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-24: "let's plug in the 3D models from Terror, the main
// character male and female models <3 and add in Willie from the Forge into
// the pier as well <3"
//
// ⚑ THE LAW OF ONE BODY. These are TERROR'S bodies, not look-alikes:
//   laostreModel3D.jsx · lyraeiModel3D.jsx · willieModel3D.jsx — copied
//   byte-identical from app-terror (md5-checked), glbs from the gamemaster
//   masters (gamemaster/models/sculpt/glb/, == Terror's public/models).
//   Never edit those three files here: re-copy them when Terror moves on.
//   In Terror the male player (player_m) IS LAOSTRE and the female (player_f)
//   IS LYRAEI; the Forge's Willie is willie_forge.glb (the frozen 2026-09-16
//   sculpt — the Plaza's Willie has since moved on to willie.glb).
// ⚑ THEY OWN THE BODY, WE OWN THE POSE. This file only chooses the body,
//   sizes it to the pier and hands it `ctl` = { facing, speed } — exactly the
//   control Terror's Plaza hands it, so it walks and turns the same.
// ⚑ THE FAIL-SAFE LAW (sculptGate.jsx, also byte-identical): while a glb loads,
//   if it fails, or when <FrameWatch/> sees the pier can't keep up, the old 2D
//   sprite stands in (`ctl.standIn`). The pier never shows nobody.
// ════════════════════════════════════════════════════════════════════
import { useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { BeingBody } from "./sculptedBeings";
import { LINEAGE_IDS, TIERS } from "./ieBeingModels";
import { LaostreSculpted } from "./laostreModel3D";
import { LyraeiSculpted } from "./lyraeiModel3D";
import { WillieForgeSculpted } from "./willieModel3D";
import { failSafe } from "./sculptGate";
import { BonnieSculpted } from "./bonnieModel3D";

// Terror's own heights (plazaFolk3D: PERSON_H 1.3 = Lyraei, AEN_M_H = VENUS_H 1.6 =
// Laostre, WILLY_H 1.4) in a world where a person is 1.3. On the pier a person is
// 1.5 tiles (PlayerEntity: tile × 1.5), so ONE factor scales all three — Terror's
// proportions between them are kept exactly.
export const PIER_PERSON = 1.5 / 1.3;
export const LYRAEI_H = 1.3 * PIER_PERSON;
export const LAOSTRE_H = 1.6 * PIER_PERSON;
export const WILLIE_H = 1.4 * PIER_PERSON;

// the stand-in: whatever the host put in ctl.standIn (the actor's own sprite)
function StandIn({ ctl }) {
  return ctl && ctl.standIn ? ctl.standIn() : null;
}
const LyraeiOnPier = failSafe(LyraeiSculpted, StandIn, LYRAEI_H);
const LaostreOnPier = failSafe(LaostreSculpted, StandIn, LAOSTRE_H);
const WillieOnPier = failSafe(WillieForgeSculpted, StandIn, WILLIE_H);

export const heroHeight = (gender) => (gender === "f" ? LYRAEI_H : LAOSTRE_H);

// ⭐ The player's body: Lyraei for "f", Laostre for everyone else (Terror's rule)
export function HeroBody({ gender, ctl }) {
  return gender === "f" ? <LyraeiOnPier ctl={ctl} /> : <LaostreOnPier ctl={ctl} />;
}

// ⭐ Willie from the Forge, standing in the hub (pierPlan INTERACTABLES "willie").
// A landmark piece: PIER_PIECES.willie → <WillieFromTheForge n={row} />.
// ⚠ FACING THE CAMERA (south), like every facade on the pier. Turned to face the
// handle he showed his back from every angle you normally walk up (visual review).
const WILLIE_CTL = { facing: { x: 0, y: 1 }, speed: 0 };
export function WillieFromTheForge({ n }) {
  return (
    <group position={[n.x, 0, n.y]}>
      <WillieOnPier ctl={WILLIE_CTL} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE COMPANION — the SAME one you had in Terror, the SAME body
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-24: "let's add real continuity, keep the same companion I
// had from the previous game, I just walked in and out of Terror my 3D STL
// turned into 2D Jaeonxi xD"
// The pier read `profile.companionId` (its own old field). Terror reads
// `profile.equippedCompanion` and falls back to the starter deck's companion
// (app-terror useEquippedCompanion.js + companions.js getStarterCompanionId).
// Both games listen to the SAME profiles/{uid} node, so the pier now resolves
// it Terror's way, and draws it with Terror's body door: sculptedBeings.jsx
// (byte-identical from app-terror) → the sculpted glb where one exists
// (spectral_tiger_lord.glb …, from the gamemaster masters), the kit's painted
// body otherwise. A companion with no body (J'aeonxi, the trophies) keeps its
// 2D picture, exactly as Terror does.
// ════════════════════════════════════════════════════════════════════

const CARD_RE = /^(.+)_(beb|champion|lord)$/;
const TIER_OF = { beb: TIERS.BEB, champion: TIERS.CHAMPION, lord: TIERS.LORD };
const baseId = (id) => (typeof id === "string" && id.includes("~") ? id.split("~")[0] : id);   // necroSeal's TAG_SEP

// Terror's resolution, in Terror's order (useEquippedCompanion.resolveCompanionId)
export function resolveCompanionId(profile) {
  if (!profile) return null;
  const eq = profile.equippedCompanion;
  if (typeof eq === "string" && eq) return eq;
  const sd = profile.starterDeckId;
  if (typeof sd === "string" && sd.startsWith("starter_")) return `${sd.slice(8)}_beb`;
  return sd ? "jynxi" : null;                                  // Terror's legacy fallback
}
// the body behind a companion id (fieldBeings3D.beingOf), or null → picture
export function companionBeing(id) {
  if (!id || typeof id !== "string") return null;
  const m = CARD_RE.exec(baseId(id));
  if (!m || LINEAGE_IDS.indexOf(m[1]) === -1) return null;
  return { lineage: m[1], tier: TIER_OF[m[2]] };
}

// at heel and smaller than its owner (the 2026-09-14 ruling on the sprite, kept)
const COMP_H = { beb: 0.8, champion: 1.0, lord: 1.25 };

// ⭐ Walks a step behind and to the side of its owner, turning and walking with
// them. `owner` is the actor's ctl ({ facing, speed }); this reads it per frame.
export function CompanionFollower({ id, owner }) {
  const being = companionBeing(id);
  const g = useRef(), fit = useRef();
  const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const refit = () => {
    const o = fit.current; if (!o || !being) return;
    o.scale.setScalar(1); o.position.y = 0; o.updateWorldMatrix(true, true);
    const b = new THREE.Box3().setFromObject(o);
    if (b.isEmpty()) return;
    const h = b.max.y - b.min.y, k = h > 0.05 ? COMP_H[being.tier] / h : 1;
    o.scale.setScalar(k); o.position.y = -b.min.y * k;
  };
  useLayoutEffect(refit);
  useFrame((_, dt) => {
    const o = g.current; if (!o) return;
    const f = owner.facing || { x: 0, y: 1 };
    ctl.facing = f; ctl.speed = owner.speed || 0;
    // behind and to the left of where the owner faces
    const tx = -f.x * 0.85 + f.y * 0.55, tz = -f.y * 0.85 - f.x * 0.55;
    const k = 1 - Math.pow(0.02, Math.min(dt || 0.016, 0.05));
    o.position.x += (tx - o.position.x) * k;
    o.position.z += (tz - o.position.z) * k;
  });
  if (!being) return null;
  return (
    <group ref={g} position={[-0.55, 0, -0.85]}>
      <group ref={fit}>
        <BeingBody lineage={being.lineage} tier={being.tier} ctl={ctl} onSkin={refit} />
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ BONNIE BY THE WAGON (caP'n, 2026-09-24): she takes Mystic Ally's place at
// the Marketplace — for now she just STANDS beside the wagon; a proper stall (or
// the rebuilt gypsy wagon itself) comes later. Facing the camera (south) like
// Willie. Sized off Lyraei: the same world metres as her sculpt (crown 1.649).
// No 2D sprite exists for her, so while the glb loads (or if the pier drops to
// the fail-safe) the spot is simply empty, as it was before her.
// ════════════════════════════════════════════════════════════════════
// caP'n 2026-09-24: "make her the same size as the STL" — the Spectral Tiger Lord companion
// stands COMP_H.lord = 1.25 on the pier. (Inside the wagon was tried on paper: its hatch is a
// solid box and its counter faces north, away from the camera, so she'd be hidden. She stands
// beside it until her own gypsy wagon is built — NEXT UP for the pier.)
export const BONNIE_H = 1.25;
const BONNIE_CTL = { facing: { x: 0, y: 1 }, speed: 0 };
const BonnieOnPier = failSafe(BonnieSculpted, () => null, BONNIE_H);
// ⭐ 2026-09-24 (later): her gypsy wagon is BUILT (pierStalls' BonnieWagonModel) and she stands IN
// it, on its floor: `y` is the wagon floor's height. The code wagon (fail-safe) still stands her beside it.
export function BonnieByTheWagon({ x, y = 0, z }) {
  return (
    <group position={[x, y, z]}>
      <BonnieOnPier ctl={BONNIE_CTL} />
    </group>
  );
}
